import {Controller} from "@hotwired/stimulus"

// ReactionsOverlayController
// --------------------------
// Global reactions picker shown ABOVE the context menu (shared/_context_menu)
// when a long-press on a Message or Comment dispatches `context-menu:open`
// with reactable metadata.
//
// Dispatch protocol (from swipe_reply_controller):
//   document.dispatchEvent(new CustomEvent("context-menu:open", {
//     detail: { src, reactableType, reactableId }
//   }))
//
// The overlay only opens when `detail.reactableType` AND `detail.reactableId`
// are present — it stays dormant for the generic context-menu path.
//
// Closing:
//   - context-menu:close event (synced with context menu)
//   - Escape key
//   - Outside click
//   - After submitting a reaction (Turbo success triggers close)
export default class extends Controller {
    static targets = ["panel", "form", "option"]
    static values = {
        postPathTemplate: String
    }

    connect() {
        this._open = false
        this._closeTimer = null
        this._currentType = null
        this._currentId = null

        this._onOpen = this._handleOpen.bind(this)
        this._onClose = this.close.bind(this)
        this._onKeydown = this._handleKeydown.bind(this)
        this._onDocClick = this._handleDocClick.bind(this)
        this._onSubmitEnd = this._handleSubmitEnd.bind(this)

        document.addEventListener("context-menu:open", this._onOpen)
        document.addEventListener("context-menu:close", this._onClose)
        document.addEventListener("keydown", this._onKeydown)
        document.addEventListener("turbo:submit-end", this._onSubmitEnd)
    }

    disconnect() {
        clearTimeout(this._closeTimer)
        document.removeEventListener("context-menu:open", this._onOpen)
        document.removeEventListener("context-menu:close", this._onClose)
        document.removeEventListener("keydown", this._onKeydown)
        document.removeEventListener("turbo:submit-end", this._onSubmitEnd)
        document.removeEventListener("click", this._onDocClick, true)
    }

    _handleOpen(event) {
        const detail = event.detail || {}
        const {reactableType, reactableId} = detail
        if (!reactableType || !reactableId) return

        this._currentType = reactableType
        this._currentId = reactableId

        this._updateForms(reactableType, reactableId)
        this.open()
    }

    _updateForms(reactableType, reactableId) {
        const template = this.postPathTemplateValue || ""
        const action = template
            .replace("User", reactableType)
            .replace("PLACEHOLDER", reactableId)

        this.formTargets.forEach((form) => {
            form.action = action
        })
    }

    open() {
        if (!this.hasPanelTarget) return

        clearTimeout(this._closeTimer)
        this.element.classList.remove("hidden")
        // The context menu opens AFTER its turbo-frame finishes loading, so we
        // must wait until its panel is actually visible (non-zero bounding rect)
        // before anchoring ourselves above it. We poll up to ~600ms with rAF.
        this._waitForContextMenu((contextRect) => {
            this._positionPanel(contextRect)
            this.panelTarget.classList.remove("scale-95", "opacity-0")
            this.panelTarget.classList.add("scale-100", "opacity-100")
            document.addEventListener("click", this._onDocClick, true)
            this._open = true
        })
    }

    _waitForContextMenu(callback, attempts = 0) {
        const MAX_ATTEMPTS = 40 // ~600ms @ 60fps
        const selector = '#context-menu [data-context-menu-target="panel"]'
        const contextPanel = document.querySelector(selector)
        const rect = contextPanel ? contextPanel.getBoundingClientRect() : null

        const ready = rect && rect.width > 0 && rect.height > 0
        if (ready || attempts >= MAX_ATTEMPTS) {
            callback(ready ? rect : null)
            return
        }
        requestAnimationFrame(() => this._waitForContextMenu(callback, attempts + 1))
    }

    close() {
        if (!this._open && this.element.classList.contains("hidden")) return

        if (this.hasPanelTarget) {
            this.panelTarget.classList.add("scale-95", "opacity-0")
            this.panelTarget.classList.remove("scale-100", "opacity-100")
        }
        document.removeEventListener("click", this._onDocClick, true)
        this._open = false

        this._closeTimer = setTimeout(() => {
            this._closeTimer = null
            if (!this._open) this.element.classList.add("hidden")
        }, 150)
    }

    _handleKeydown(event) {
        if (event.key === "Escape" && this._open) this.close()
    }

    _handleDocClick(event) {
        if (!this._open) return
        if (this.panelTarget.contains(event.target)) return
        // Let context_menu_controller handle its own overlay-click dismissal;
        // we just close ourselves in sync.
        this.close()
    }

    _handleSubmitEnd(event) {
        // Close when a reaction submit from this overlay completes successfully.
        const form = event.target
        if (!form || !this.formTargets.includes(form)) return
        if (event.detail && event.detail.success === false) return
        this.close()
        // Also close the underlying context menu — the user has picked an action.
        document.dispatchEvent(new CustomEvent("context-menu:close"))
    }

    _positionPanel(contextRect) {
        const panel = this.panelTarget
        const viewportW = window.innerWidth || document.documentElement.clientWidth
        const viewportH = window.innerHeight || document.documentElement.clientHeight
        const GAP = 12
        const MARGIN = 12

        // Measure the overlay panel
        panel.style.visibility = "hidden"
        panel.style.top = "0px"
        panel.style.left = "0px"
        const panelW = panel.offsetWidth
        const panelH = panel.offsetHeight
        panel.style.visibility = ""

        let centerX
        let targetTop

        if (contextRect) {
            centerX = contextRect.left + contextRect.width / 2
            targetTop = contextRect.top - panelH - GAP
        } else {
            // Fallback — no context menu rendered (shouldn't happen in practice
            // since we wait for it, but keep the UI sane).
            centerX = viewportW / 2
            targetTop = viewportH / 2 - panelH - GAP
        }

        let left = centerX - panelW / 2
        if (left < MARGIN) left = MARGIN
        if (left + panelW > viewportW - MARGIN) left = viewportW - MARGIN - panelW
        if (targetTop < MARGIN) targetTop = MARGIN

        panel.style.left = `${Math.round(left)}px`
        panel.style.top = `${Math.round(targetTop)}px`
    }
}
